import { useTranslation } from 'next-i18next'
import { useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import { removePointsAndParenthesis } from '~/bootcamp/Activities/Algebra/Algebra.utils'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useCoefficientInput from './useCoefficientInput'

function CoefficientInput({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const { operators, variable, formValid, inputRef, answer, onInputChange, handleCheckAnswer, goToNextQuestion } =
    useCoefficientInput({
      difficultyLevel,
      onAnswerStateChange,
      onNextQuestionClick,
      answerState,
      autofocus,
    })

  useEmitInstance(
    onInstanceChange,
    variable
      ? `${operators
          .map((operator, index) => (index !== 0 || operator === '-' ? ` ${operator} ${variable}` : variable))
          .join('')} = \\square ${variable}`
      : null,
  )

  const [inputKey, setInputKey] = useState<string>(uuid())

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex-col">
        <div className="flex flex-row flex-wrap justify-center items-center mt-2">
          <span className="tracking-widest">
            {operators.map((operator, index) => (
              <span key={operator + index.toString()}>
                {(index !== 0 || operator === '-') && operator}
                <i className="mx-1">{variable}</i>
              </span>
            ))}
          </span>
          <span className="block sm:inline-block">
            <Equals />
            <Input
              aria-label={t('ariaLabelButtons.insertCoefficientValue')}
              autoFocus={autofocus}
              isError={answerState === false}
              key={inputKey}
              maxDigits={3}
              onChange={(value) => onInputChange(removePointsAndParenthesis(value))}
              onEnter={onEnterPress}
              ref={inputRef}
              type="text"
              value={answer}
            />
            <i className="ml-1">{variable}</i>
          </span>
        </div>
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(''),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default CoefficientInput
